import { useState, useCallback } from 'react';
import { useAppAuth } from '@/hooks/useAppAuth';
import { Button } from '@/components/ui/button';
import { Loader2, User } from 'lucide-react';
import SnowflakeIcon from '@/components/icons/SnowflakeIcon';
import { showToast } from '@/services/toast.service';
import TenantConfigService from '@/services/tenantConfig.service';

interface SnowflakeSsoLoginButtonProps {
  tenantConfigId: string;
  showEmail?: boolean;
}

export default function SnowflakeSsoLoginButton({
  tenantConfigId,
  showEmail = true,
}: SnowflakeSsoLoginButtonProps) {
  const { user } = useAppAuth();
  const currentUserEmail = user?.email?.toLowerCase() ?? '';
  const [loggingIn, setLoggingIn] = useState(false);

  const handleLogin = useCallback(async () => {
    if (!tenantConfigId) return;
    setLoggingIn(true);

    try {
      const { saml_response, acs_url } =
        await TenantConfigService.getInstance().snowflakeSsoLogin(tenantConfigId);

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = acs_url;
      form.target = '_blank';

      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'SAMLResponse';
      input.value = saml_response;
      form.appendChild(input);

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    } catch (err: any) {
      showToast.error('Login failed', {
        description: err.message || 'Could not generate SSO login',
      });
    } finally {
      setLoggingIn(false);
    }
  }, [tenantConfigId]);

  return (
    <div>
      <Button
        onClick={handleLogin}
        disabled={loggingIn}
        className="bg-[#29b5e8] hover:bg-[#1a9fd4] text-white"
      >
        {loggingIn ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            Logging in...
          </>
        ) : (
          <>
            <SnowflakeIcon className="h-4 w-4 mr-2" />
            Login to Snowflake
          </>
        )}
      </Button>
      {showEmail && (
        <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
          <User className="h-3 w-3" />
          {currentUserEmail}
        </p>
      )}
    </div>
  );
}
